/**
 * RecipeShelfCard
 *
 * Card used inside horizontal scrolling shelves.
 * Displays recipe image/gradient, title, cook time, and diet indicator.
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LinearGradient } from 'expo-linear-gradient';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Feather } from '@expo/vector-icons';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import { PersonalRecipeIndexEntry } from '../../services/personalRecipeIndex';
import { SourceRatingBadge } from '../shared/SourceRatingBadge';
import { RemoteImage } from '../shared/RemoteImage';
import { classifyDietKind, DietKind } from '../../utils/dietClassifier';
import { selectCurrentLanguage } from '../../redux/languageSlice';
import { resolveRecipeTitle } from '../../utils/recipeTitle';
import { recipeCardTimes } from '../../utils/recipeTimeFacts';

/**
 * Why this card is being shown. Set only by "L'idée du jour", where each card
 * answers a different need and the reason is the whole point; the plain shelves
 * leave it undefined and render exactly as before.
 */
export interface RecipeCardReason {
  /** Angle name, e.g. "Utilise votre frigo" */
  label: string;
  /** The consequence line, e.g. "3 ingrédients déjà chez vous" */
  text: string;
  /** Angle accent from FAN_REASONS */
  accent: string;
}

/**
 * One line under the title saying why a search put the recipe where it is
 * (#847): when the household added it, or what of the pantry it uses.
 */
export interface RecipeCardNote {
  text: string;
  /** `pantry` reads in the in-stock green, like the pantry marks elsewhere. */
  tone?: 'muted' | 'pantry';
}

interface RecipeShelfCardProps {
  recipe: PersonalRecipeIndexEntry;
  personalRating?: number; // 1-5, from recipeMetadataSlice
  onPress: () => void;
  width?: number | `${number}%`; // overrides default CARD_WIDTH (156)
  isSelected?: boolean;    // highlights border with brand color
  reason?: RecipeCardReason;
  note?: RecipeCardNote;
  testID?: string;
}

/**
 * Detect diet type from recipe metadata.
 *
 * Delegates to the canonical classifier. This used to be a local copy that read
 * the `diets` tag plus an English-only fish regex; tag coverage on scraped
 * recipes is near zero, so over the 481-recipe bundled corpus it returned
 * **445 meat / 36 fish / 0 veg** where `classifyDietKind` returns
 * 316 / 90 / 75 — a 30% disagreement and no vegetarian recipe ever tinted as
 * one. The duplicate is gone rather than fixed in place, so the card tint and
 * the placeholder wash can never drift apart again.
 */
export function detectRecipeDiet(recipe: PersonalRecipeIndexEntry): DietKind {
  return classifyDietKind({
    title: recipe.title ?? '',
    ingredients: recipe.ingredients ?? [],
    diets: recipe.diets,
  });
}

const GRADIENT_TONES: Record<DietKind, [string, string, string]> = {
  veg: ['#aec27a', '#7e9a4f', '#566f36'],
  fish: ['#f0b39a', '#d57f63', '#b25840'],
  meat: ['#e8a35e', '#c06b34', '#9a4a22'],
};

const DIET_DOT_COLORS: Record<DietKind, string> = {
  veg: '#5d7c43',
  fish: '#cf6f56',
  meat: Paprika.primary,
};

const IMAGE_HEIGHT = 108;
const CARD_WIDTH = 156;

const RecipeShelfCard: React.FC<RecipeShelfCardProps> = ({
  recipe,
  personalRating,
  onPress,
  width,
  isSelected,
  reason,
  note,
  testID,
}) => {
  // Resolved here rather than passed in: this card renders in the browse grids,
  // the shelves and the weekly library, and every one of them wants the title
  // in the app language.
  const language = useSelector(selectCurrentLanguage);
  const { t } = useTranslation();

  const diet = detectRecipeDiet(recipe);
  const gradientColors = GRADIENT_TONES[diet];
  const dietDotColor = DIET_DOT_COLORS[diet];

  // Our own rating is no longer drawn as a number of stars — that glyph belongs
  // to the source site's rating, and having both on one card made a 4 ambiguous.
  // A loved recipe gets a heart instead; anything below simply shows nothing.
  const showLoved = personalRating != null && personalRating >= 4;
  const showSourceRating = !recipe.sourceUrl && recipe.sourceRating != null && recipe.sourceRating > 0;

  const times = recipeCardTimes(recipe, t);

  return (
    <TouchableOpacity
      style={[
        styles.container,
        width !== undefined ? { width } : undefined,
        isSelected ? { borderColor: Paprika.primary, borderWidth: 2 } : undefined,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
      testID={testID}
    >
      {/* Angle strip — only on "L'idée du jour", where the reason is the point */}
      {reason && (
        <View style={[styles.reasonStrip, { backgroundColor: `${reason.accent}14` }]}>
          <View style={[styles.reasonDot, { backgroundColor: reason.accent }]} />
          <Text style={[styles.reasonLabel, { color: reason.accent }]} numberOfLines={1}>
            {reason.label}
          </Text>
        </View>
      )}

      {/* Image Area */}
      <View style={styles.imageArea}>
        <RemoteImage
          uri={recipe.image}
          variant="thumb"
          style={styles.image}
          resizeMode="cover"
          fallback={
            <LinearGradient
              colors={gradientColors}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.gradient}
            />
          }
        />

        {/* Corner badge — the household loved it, or (with no source link to
            carry it) the source site's own rating. Never both. */}
        {showLoved ? (
          <View style={styles.ratingBadge}>
            <Feather name="heart" size={11} color={Paprika.approvalYes} />
          </View>
        ) : showSourceRating ? (
          <View style={styles.ratingBadge}>
            <Text style={styles.ratingText}>★ {recipe.sourceRating!}</Text>
          </View>
        ) : null}
      </View>

      {/* Info Area */}
      <View style={styles.infoArea}>
        {/* Title */}
        <Text style={styles.title} numberOfLines={2}>
          {resolveRecipeTitle(recipe, language)}
        </Text>

        {!!note && (
          <Text
            style={[styles.note, note.tone === 'pantry' ? styles.notePantry : undefined]}
            numberOfLines={1}
          >
            {note.text}
          </Text>
        )}

        {/* Source attribution + rating */}
        {!!recipe.sourceUrl && (
          <View style={styles.sourceSection}>
            <SourceRatingBadge
              sourceUrl={recipe.sourceUrl}
              sourceRating={recipe.sourceRating}
              sourceRatingCount={recipe.sourceRatingCount}
              size="sm"
            />
          </View>
        )}

        {/* Bottom Row: Diet Dot + time. « Préparation » sits on its own line above
            « Prêt en » when the recipe has a timeline: both in one line wrap in
            three on a 156px card. */}
        <View style={styles.bottomRow}>
          <View style={[styles.dietDot, { backgroundColor: dietDotColor }]} />
          {times.ready != null && (
            <View style={styles.timeColumn} testID="recipe-card-times">
              {times.handsOn != null && (
                <View style={styles.timeRow}>
                  <Ionicons name="hand-left-outline" size={11} color={Paprika.textMuted} />
                  <Text style={styles.timeText}>{times.handsOn}</Text>
                </View>
              )}
              <View style={styles.timeRow}>
                <Ionicons name="time-outline" size={11} color={Paprika.textMuted} />
                <Text style={styles.timeText}>{times.ready}</Text>
              </View>
            </View>
          )}
        </View>

        {/* The concrete consequence behind the angle */}
        {reason?.text ? (
          <Text style={[styles.reasonText, { color: reason.accent }]} numberOfLines={2}>
            {reason.text}
          </Text>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    width: CARD_WIDTH,
    borderRadius: 18,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#f3ebe1',
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: '#52443c',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0 2px 8px rgba(82,68,60,0.08)',
      },
    }),
  } as ViewStyle,
  imageArea: {
    height: IMAGE_HEIGHT,
    overflow: 'hidden',
    position: 'relative',
  } as ViewStyle,
  reasonStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 6,
  } as ViewStyle,
  reasonDot: {
    width: 5,
    height: 5,
    borderRadius: PaprikaRadius.full,
  } as ViewStyle,
  reasonLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10.5,
    letterSpacing: 0.2,
    flexShrink: 1,
  } as TextStyle,
  reasonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    lineHeight: 14,
    marginTop: 5,
  } as TextStyle,
  image: {
    width: '100%',
    height: '100%',
  },
  gradient: {
    width: '100%',
    height: '100%',
  } as ViewStyle,
  ratingBadge: {
    position: 'absolute',
    top: 9,
    right: 9,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: PaprikaRadius.full,
    backgroundColor: 'rgba(255,255,255,0.92)',
    ...Platform.select({
      ios: {
        shadowColor: '#52443c',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
      },
      android: {
        elevation: 1,
      },
      web: {
        boxShadow: '0 1px 4px rgba(82,68,60,0.06)',
      },
    }),
  } as ViewStyle,
  ratingText: {
    fontSize: 11,
    fontWeight: '600',
    color: Paprika.textPrimary,
  } as TextStyle,
  note: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    lineHeight: 14,
    color: Paprika.textMuted,
    marginTop: -3,
    marginBottom: 7,
  } as TextStyle,
  notePantry: {
    fontFamily: 'Inter-Medium',
    color: Paprika.approvalYes,
  } as TextStyle,
  sourceSection: {
    marginBottom: 6,
  } as ViewStyle,
  infoArea: {
    paddingTop: 11,
    paddingHorizontal: 12,
    paddingBottom: 13,
  } as ViewStyle,
  title: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    color: Paprika.textPrimary,
    minHeight: 35,
    marginBottom: 8,
  } as TextStyle,
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 4,
  } as ViewStyle,
  dietDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    marginTop: 4,
  } as ViewStyle,
  timeColumn: {
    flex: 1,
    gap: 3,
  } as ViewStyle,
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  } as ViewStyle,
  timeText: {
    flexShrink: 1,
    fontSize: 11,
    lineHeight: 15,
    color: Paprika.textMuted,
  } as TextStyle,
});

export default RecipeShelfCard;
